/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Popper, Paper } from '@mui/material';
import type { FC } from 'react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CustomIconButton } from '@opengeoweb/shared';
import { Options } from '@opengeoweb/theme';
import { OptionsMenu } from './OptionsMenu';
import {
  TimeStepShortcutContext,
  useTimeStepShortcutListener,
} from './optionsMenuShortcut';
import type { TimeStepDirection } from './optionsMenuShortcut';
import { useTimesliderTranslation } from '../../../utils/i18n';

interface OptionsMenuButtonProps {
  speedBtn?: React.ReactNode;
  timeStepBtn?: React.ReactNode;
  timeSpanBtn?: React.ReactNode;
  animationLengthBtn?: React.ReactNode;
  isOpenByDefault?: boolean;
  enableKeyboardShortcut?: boolean;
}

export const OptionsMenuButton: FC<OptionsMenuButtonProps> = ({
  speedBtn,
  timeStepBtn,
  timeSpanBtn,
  animationLengthBtn,
  isOpenByDefault = false,
  enableKeyboardShortcut = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pendingDirection, setPendingDirection] =
    useState<TimeStepDirection | null>(null);
  const openedByShortcutRef = useRef(false);
  const { t } = useTimesliderTranslation();

  useEffect(() => {
    if (anchorEl && isOpenByDefault) {
      setOpen(true);
    }
  }, [anchorEl, isOpenByDefault]);

  useTimeStepShortcutListener(enableKeyboardShortcut, (direction) => {
    setOpen((wasOpen) => {
      if (!wasOpen) {
        openedByShortcutRef.current = true;
      }
      return true;
    });
    setPendingDirection(direction);
  });

  const shortcutContextValue = useMemo(
    () => ({
      pendingDirection,
      closeOptionsMenu: (): void => {
        if (openedByShortcutRef.current) {
          setOpen(false);
        }
        openedByShortcutRef.current = false;
        setPendingDirection(null);
      },
      consume: (): void => setPendingDirection(null),
    }),
    [pendingDirection],
  );

  return (
    <div>
      <CustomIconButton
        aria-label="animation options"
        data-testid="optionsMenuButton"
        isSelected={open}
        onClick={(): void => {
          openedByShortcutRef.current = false;
          setOpen(!open);
        }}
        ref={(ref): void => {
          setAnchorEl(ref);
        }}
        tooltipProps={{
          title: t('timeslider-animation-options'),
          placement: 'bottom',
        }}
        variant="tool"
      >
        <Options />
      </CustomIconButton>
      <Popper
        anchorEl={anchorEl}
        data-testid="optionsMenuPopOver"
        open={open}
        placement="top-start"
        sx={{ zIndex: 10 }}
      >
        <Paper elevation={3} sx={{ position: 'relative', top: '-2rem' }}>
          <Grid
            container
            spacing={1}
            sx={{
              padding: '10px',
            }}
          >
            <TimeStepShortcutContext.Provider value={shortcutContextValue}>
              <OptionsMenu
                animationLengthBtn={animationLengthBtn}
                speedBtn={speedBtn}
                timeSpanBtn={timeSpanBtn}
                timeStepBtn={timeStepBtn}
              />
            </TimeStepShortcutContext.Provider>
          </Grid>
        </Paper>
      </Popper>
    </div>
  );
};
